import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { CircleAlert, ExternalLink } from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { adminRecordInspector } from 'rails_routes'
import { Badge, Switch } from 'slab'
import { getLocaleDateString } from 'utils'
import { useGetBrokersMembersMemberIdQualifyingLifeEvents } from 'utils/web_api'
import {
  type BrokersQualifyingLifeEvent,
  type GetBrokersMembersMemberIdQualifyingLifeEventsParams,
} from 'utils/web_api.schemas'

type QualifyingLifeEventsTableProps = {
  planYear?: number
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  memberId: string
}

const columnHelper = createColumnHelper<BrokersQualifyingLifeEvent>()

const StateBadge = ({ state }: { state: string }) => {
  switch (state) {
    case 'in_progress':
      return <Badge type='neutral'>In Progress</Badge>
    case 'awaiting_confirmation':
      return <Badge type='info'>Awaiting Confirmation</Badge>
    case 'complete':
      return <Badge type='success'>Complete</Badge>
    default:
      return <Badge type='neutral'>{state}</Badge>
  }
}

const QualifyingLifeEventsTable = ({
  planYear,
  pagination,
  setPagination,
  memberId,
}: QualifyingLifeEventsTableProps) => {
  // Manage filter state locally, with planYear coming from props
  const [includeInProgress, setIncludeInProgress] = useState(false)

  // Build filters from planYear prop and local state
  const filters = useMemo<GetBrokersMembersMemberIdQualifyingLifeEventsParams>(() => ({
    include_in_progress: includeInProgress,
    ...(planYear && { plan_year: planYear }),
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
  }), [planYear, includeInProgress, pagination.pageIndex, pagination.pageSize])

  const {
    data: qualifyingLifeEventsData,
    error,
    isLoading,
  } = useGetBrokersMembersMemberIdQualifyingLifeEvents(
    memberId,
    filters,
    {
      query: {
        enabled: !!memberId && !!filters.plan_year,
        refetchOnWindowFocus: false,
      },
    }
  )

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  // Memoize the columns to avoid re-rendering the table.
  const columns = useMemo(() => {
    return [
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => (
          <Badge
            onActionClick={() =>
              window.open(adminRecordInspector.show({ id: info.getValue() }))}
            actionIcon={ExternalLink}
            actionLabel='Inspect'
            type='neutral'
          >
            {info.getValue()?.slice(-6)}
          </Badge>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: () => <div className='w-min'>Created</div>,
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
      columnHelper.accessor('effective_date', {
        header: () => <div className='w-min'>Estimated Effective Date</div>,
        cell: (info) => {
          const date = info.getValue()
          return date ? getLocaleDateString(new Date(date)) : '—'
        },
      }),
      columnHelper.accessor('state', {
        header: () => <div className='w-min'>State</div>,
        cell: (info) => <StateBadge state={info.getValue()} />,
      }),
      columnHelper.accessor('situation', {
        header: () => <div className='w-min'>Situation</div>,
        cell: (info) => <Badge type='neutral'>{info.getValue()}</Badge>,
      }),
      columnHelper.accessor('includes_medical', {
        header: () => <div className='w-min'>Includes Medical</div>,
        cell: (info) => (
          <Badge type={info.getValue() ? 'success' : 'neutral'}>
            {info.getValue() ? 'Yes' : 'No'}
          </Badge>
        ),
      }),
      columnHelper.accessor('includes_dental', {
        header: () => <div className='w-min'>Includes Dental</div>,
        cell: (info) => (
          <Badge type={info.getValue() ? 'success' : 'neutral'}>
            {info.getValue() ? 'Yes' : 'No'}
          </Badge>
        ),
      }),
      columnHelper.accessor('includes_vision', {
        header: () => <div className='w-min'>Includes Vision</div>,
        cell: (info) => (
          <Badge type={info.getValue() ? 'success' : 'neutral'}>
            {info.getValue() ? 'Yes' : 'No'}
          </Badge>
        ),
      }),
      columnHelper.accessor('confirmation_sent_at', {
        header: () => <div className='w-min'>Confirmation Sent</div>,
        cell: (info) => {
          const date = info.getValue()
          const state = info.row.original.state
          const hasAttachments = info.row.original.has_attachments

          // If the QLE is complete and has attachments, show the confirmation sent date. We only
          // send confirmation emails if the QLE has attachments (aka v2 QLE flow).
          if (state === 'complete' && hasAttachments) {
            if (!date) {
              return (
                <DisplayTooltipOnHover text='Confirmation not sent. Dependents must be manually added to the plan dependents before mailer can be sent.'>
                  <Badge type='warning' leftIcon={CircleAlert}>
                    Error
                  </Badge>
                </DisplayTooltipOnHover>
              )
            }

            return getLocaleDateString(new Date(date))
          }

          return '—'
        },
      }),
    ]
  }, [])

  const table = useReactTable({
    data: qualifyingLifeEventsData?.data ?? [],
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  useEffect(() => {
    if (qualifyingLifeEventsData) {
      setPaginationMetadata(qualifyingLifeEventsData.pagination)
    }
  }, [qualifyingLifeEventsData?.pagination])

  return (
    <div className='space-y-4'>
      <div className='flex justify-end'>
        <Switch
          label='Include In Progress'
          value={includeInProgress ? 'true' : 'false'}
          onChange={(e) => setIncludeInProgress(e.currentTarget.checked)}
        />
      </div>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={!!error}
        noDataMessage='No dependent additions found.'
        defaultSortingState={[]}
        paginationLabelSingular='dependent addition'
        paginationMetadata={paginationMetadata}
        tableMessageHeightClass='h-32'
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
    </div>
  )
}

export default QualifyingLifeEventsTable
